/**
 * Admin → Invites (iter 88)
 *
 * One page to invite real clinics + vendors with a one-click magic-link
 * (instead of distributing temp passwords). The link works for 14 days
 * and lands the recipient directly in the right portal.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Send,
  Stethoscope,
  UserPlus,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed — select the link manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-[11px] font-semibold px-2 py-1 text-slate-200 hover:bg-slate-700"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}

function InviteForm({ kind, onCreated, onClose }) {
  const isClinic = kind === "clinic";
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [contactName, setContactName] = useState("");
  const [geography, setGeography] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!email.trim() || !orgName.trim()) {
      toast.error(`Email + ${isClinic ? "clinic" : "vendor"} name are required`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = isClinic
        ? {
            email: email.trim(),
            clinic_name: orgName.trim(),
            name: contactName.trim() || null,
            send_email: sendEmail,
          }
        : {
            email: email.trim(),
            vendor_name: orgName.trim(),
            name: contactName.trim() || null,
            vendor_geography: geography.trim()
              ? geography
                  .split(",")
                  .map((s) => s.trim().toUpperCase())
                  .filter(Boolean)
              : null,
            send_email: sendEmail,
          };
      const r = await api.post(
        isClinic ? "/admin/invites/clinic" : "/admin/invites/vendor",
        payload
      );
      toast.success(
        sendEmail ? "Invite sent. Recipient should receive an email." : "Invite created."
      );
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`invite-form-${kind}`}
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isClinic ? <Stethoscope className="w-4 h-4 text-blue-400" /> : <Building2 className="w-4 h-4 text-amber-400" />}
          <div className="text-sm font-bold text-slate-100">
            Invite a {isClinic ? "clinic" : "vendor"}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-200 p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-400">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={`invite-${kind}-email`}
            className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-100"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-400">
            {isClinic ? "Clinic name" : "Vendor org name"}
          </span>
          <input
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            data-testid={`invite-${kind}-org`}
            className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-100"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-400">Contact name (optional)</span>
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            data-testid={`invite-${kind}-contact`}
            className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-100"
          />
        </label>
        {!isClinic && (
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-400">
              Service states (optional, comma-sep e.g. CA, NY, TX)
            </span>
            <input
              type="text"
              value={geography}
              onChange={(e) => setGeography(e.target.value)}
              data-testid={`invite-${kind}-geography`}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-100"
            />
          </label>
        )}
        <label className="flex items-center gap-2 text-xs text-slate-300 select-none">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid={`invite-${kind}-send-email`}
            className="accent-blue-500"
          />
          <span>Email the magic-link to the recipient via Resend</span>
        </label>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-slate-400 hover:text-slate-200 px-3 py-1.5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid={`invite-${kind}-submit`}
          className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {submitting ? "Creating…" : "Create invite"}
        </button>
      </div>
    </div>
  );
}

function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  return (
    <div
      data-testid="invite-result-banner"
      className="rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-4 mb-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-xs font-bold text-emerald-200 mb-1 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Invite created for {result.email}
          </div>
          <p className="text-xs text-emerald-100/85 mb-3">
            {result.email_sent
              ? "Email was sent via Resend."
              : "Email was NOT sent — copy the link below and share it manually."}{" "}
            The link expires {fmtDate(result.expires_at)}.
          </p>
          <div className="flex items-center gap-2 text-[11px] text-slate-300 font-mono bg-slate-950/50 border border-slate-800 rounded-md p-2 break-all">
            {result.portal_link}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <CopyButton value={result.portal_link} testid="invite-result-copy" />
          <a
            href={result.portal_link}
            target="_blank"
            rel="noreferrer"
            data-testid="invite-result-open"
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-[11px] font-semibold px-2 py-1 text-slate-200 hover:bg-slate-700"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </a>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[10px] text-slate-500 hover:text-slate-300"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminInvites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // "clinic" | "vendor" | null
  const [latestResult, setLatestResult] = useState(null);
  const [resending, setResending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invites");
      setInvites(r.data?.invites || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreated = (result) => {
    setLatestResult(result);
    setOpenForm(null);
    load();
  };

  const resend = async (invite) => {
    setResending((p) => ({ ...p, [invite.id]: true }));
    try {
      const r = await api.post(`/admin/invites/${invite.id}/resend`);
      setLatestResult({
        email: invite.email,
        portal_link: r.data.portal_link,
        expires_at: r.data.expires_at,
        email_sent: r.data.email_sent,
      });
      toast.success("Fresh link minted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResending((p) => ({ ...p, [invite.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Admin · Invites
            </div>
            <h1 className="mt-1 text-3xl font-bold">Portal invites</h1>
            <p className="mt-2 text-sm text-slate-400 max-w-3xl">
              Onboard real clinics + vendors with a one-click magic-link.
              Each invite creates the org + user record and returns a 14-day
              single-use URL that drops the recipient straight into their portal
              — no temp password to share.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              data-testid="invites-refresh"
              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setOpenForm("clinic")}
              data-testid="invites-new-clinic"
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700"
            >
              <Stethoscope className="w-3.5 h-3.5" /> Invite clinic
            </button>
            <button
              type="button"
              onClick={() => setOpenForm("vendor")}
              data-testid="invites-new-vendor"
              className="inline-flex items-center gap-1 rounded-full bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700"
            >
              <Building2 className="w-3.5 h-3.5" /> Invite vendor
            </button>
          </div>
        </div>

        <ResultBanner result={latestResult} onDismiss={() => setLatestResult(null)} />

        {openForm && (
          <div className="mb-6">
            <InviteForm kind={openForm} onCreated={onCreated} onClose={() => setOpenForm(null)} />
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : invites.length === 0 ? (
          <div
            data-testid="invites-empty"
            className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-12 text-center"
          >
            <UserPlus className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <div className="text-base font-semibold text-slate-200">No invites yet</div>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
              Click <span className="font-semibold">Invite clinic</span> or <span className="font-semibold">Invite vendor</span> to onboard your first organization.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <table className="w-full text-sm" data-testid="invites-table">
              <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Organization</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Expires</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {invites.map((inv) => {
                  const isResending = resending[inv.id];
                  return (
                    <tr key={inv.id} data-testid={`invites-row-${inv.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-100">{inv.name || inv.email}</div>
                        <div className="text-[11px] text-slate-500">{inv.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            inv.role === "clinic"
                              ? "bg-blue-500/15 border-blue-500/40 text-blue-200"
                              : "bg-amber-500/15 border-amber-500/40 text-amber-200"
                          }`}
                        >
                          {inv.role === "clinic" ? (
                            <Stethoscope className="w-2.5 h-2.5" />
                          ) : (
                            <Building2 className="w-2.5 h-2.5" />
                          )}
                          {inv.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{inv.org_name}</td>
                      <td className="px-4 py-3 text-[11px] text-slate-500">
                        <div>{fmtDate(inv.created_at)}</div>
                        {inv.email_delivered_attempt ? (
                          <div className="text-emerald-300 inline-flex items-center gap-0.5">
                            <Mail className="w-2.5 h-2.5" /> emailed
                          </div>
                        ) : inv.send_email_requested ? (
                          <div className="text-amber-300">queued</div>
                        ) : (
                          <div className="text-slate-500">link only</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-slate-500">{fmtDate(inv.expires_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <CopyButton value={inv.portal_link} testid={`invites-copy-${inv.id}`} />
                          <button
                            type="button"
                            onClick={() => resend(inv)}
                            disabled={isResending}
                            data-testid={`invites-resend-${inv.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-600/15 border border-blue-500/40 text-blue-200 text-[11px] font-semibold px-2 py-1 hover:bg-blue-600/25 disabled:opacity-50"
                          >
                            {isResending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Resend
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
